import type { ReactNode } from "react";
import { CButton } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilReload } from "@coreui/icons";
import type { ApiError } from "../../api/client";
import { useProvisionCustomer } from "../../api/hooks/useProvisionCustomer";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { FailureCard } from "../common/FailureCard";
import { LegalAcceptanceDialog } from "../legal/LegalAcceptanceDialog";
import { currentTermsVersion } from "../../pages/legal/legal-content";

interface CustomerProvisionGateProps {
  children: ReactNode;
}

/**
 * Ensures the authenticated customer is provisioned (get-or-create) before the app renders and
 * fans out its data requests. Provisioning is a single awaited call here rather than the N
 * concurrent implicit creates the old server middleware did on first sign-in, so the race is gone.
 *
 * It is also where the Terms are accepted. The Terms open by saying acceptance happens at
 * registration, so there has to be a moment where it does — and this is the first point at which an
 * authenticated identity and a customer record both exist. The provision response already carries
 * the version the customer accepted, so the check costs nothing: no extra request, and the gate was
 * already a blocking await.
 *
 * Note that the acceptance dialog does NOT hold back the app's data requests the way provisioning
 * does — the app renders behind it, inert, so the customer has something to look at. Those requests
 * are for a customer who is already authenticated and provisioned, and they are reading their own
 * data, so serving them a moment early costs nothing.
 */
export function CustomerProvisionGate({ children }: CustomerProvisionGateProps) {
  const { data, isPending, isError, error, refetch } = useProvisionCustomer();

  if (isPending) {
    return <LoadingSpinner />;
  }

  if (isError) {
    const detail = error instanceof Error ? error.message : null;

    return (
      <FailureCard
        title="We couldn't finish setting up your account"
        message="This is usually a brief hiccup. Try again in a moment."
        reference={(error as ApiError)?.traceId}
        details={detail}
      >
        <CButton color="primary" onClick={() => refetch()}>
          <CIcon icon={cilReload} className="me-2" />
          Try again
        </CButton>
      </FailureCard>
    );
  }

  // Compared against the version this build renders, not against a boolean. A customer who accepted
  // an earlier text has not accepted this one, and the substance is what moves the marker — the
  // documents' lastUpdated is written by hand precisely so a corrected typo does not re-prompt
  // everybody.
  if (data?.termsAcceptedVersion !== currentTermsVersion) {
    return (
      <>
        {/*
          The app renders behind the dialog rather than being replaced by it, so the customer sees
          the portal they are signing in to rather than an empty page. It is decoration and must not
          be usable: `inert` takes the whole subtree out of the tab order, out of the accessibility
          tree and out of reach of a click, so there is no way past the dialog by keyboard. The blur
          is in custom.scss.

          The dialog itself is unaffected — CoreUI portals a modal and its backdrop to document.body,
          so both sit outside this wrapper.
        */}
        <div className="legal-acceptance-behind" inert>
          {children}
        </div>
        {/* Communication preferences are only ever set here, and this dialog always requires
            accepting the Terms — so "has never accepted anything" is the same question as "has never
            been asked about email". A later Terms revision must not re-ask. */}
        <LegalAcceptanceDialog firstAcceptance={data?.termsAcceptedVersion == null} />
      </>
    );
  }

  return <>{children}</>;
}
